import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["appearance", "fireEvent", "id", "origin", "renderers", "resolve", "actionOptions", "response", "aiSummaryConfig"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { extractAri, extractDateCreated, extractDateUpdated, extractLink, extractPersonCreatedBy, extractPersonOwnedBy, extractSmartLinkAri, extractSmartLinkCreatedBy, extractSmartLinkCreatedOn, extractSmartLinkModifiedBy, extractSmartLinkModifiedOn, extractSmartLinkTitle, extractSmartLinkUrl, extractTitle, extractType } from '@atlaskit/link-extractors';
import { fg } from '@atlaskit/platform-feature-flags';
import { extractSummary } from '../common/primitives';
import { extractFlexibleCardActions } from './actions';
import { extractPersonsUpdatedBy } from './collaboratorGroup';
import extractPreview, { extractSmartLinkPreviewImage } from './extract-preview';
import extractPriority from './extract-priority';
import extractState from './extract-state';
import { extractLinkIcon, extractSmartLinkIcon } from './icon';
import extractProviderIcon, { extractSmartLinkProviderIcon } from './icon/extract-provider-icon';
import { extractLatestCommit } from './latest-commit';
import { extractAppliedToComponentsCount, extractAssignedTo, extractAttachmentCount, extractChecklistProgress, extractCommentCount, extractCreatedBy, extractDueOn, extractLocation, extractModifiedBy, extractOwnedBy, extractPersonAssignedToAsArray, extractProgrammingLanguage, extractReactCount, extractReadTime, extractSentOn, extractSourceBranch, extractStoryPoints, extractSubscriberCount, extractSubTasksProgress, extractTargetBranch, extractViewCount, extractVoteCount } from './utils';
var extractFlexibleUiContextFromJsonLd = function extractFlexibleUiContextFromJsonLd() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var appearance = _ref.appearance,
    fireEvent = _ref.fireEvent,
    id = _ref.id,
    origin = _ref.origin,
    renderers = _ref.renderers,
    resolve = _ref.resolve,
    actionOptions = _ref.actionOptions,
    response = _ref.response,
    aiSummaryConfig = _ref.aiSummaryConfig,
    props = _objectWithoutProperties(_ref, _excluded);
  if (!response) {
    return undefined;
  }
  var data = response.data;
  var url = extractLink(data);
  return _objectSpread({
    actions: extractFlexibleCardActions({
      actionOptions: actionOptions,
      aiSummaryConfig: aiSummaryConfig,
      appearance: appearance,
      fireEvent: fireEvent,
      id: id,
      origin: origin,
      response: response,
      url: props.url // Use the original URL in edge cases, such as short links for AI summary and copy link actions.
    }),
    appliedToComponentsCount: extractAppliedToComponentsCount(data),
    assignedToGroup: extractPersonAssignedToAsArray(data),
    attachmentCount: extractAttachmentCount(data),
    authorGroup: extractPersonCreatedBy(data),
    ownedByGroup: extractPersonOwnedBy(data),
    collaboratorGroup: extractPersonsUpdatedBy(data),
    commentCount: extractCommentCount(data),
    viewCount: extractViewCount(data),
    reactCount: extractReactCount(data),
    voteCount: extractVoteCount(data),
    checklistProgress: extractChecklistProgress(data),
    createdBy: extractCreatedBy(data),
    ownedBy: extractOwnedBy(data),
    assignedTo: extractAssignedTo(data),
    createdOn: extractDateCreated(data),
    dueOn: extractDueOn(data),
    latestCommit: extractLatestCommit(data),
    linkIcon: extractLinkIcon(response, renderers),
    location: extractLocation(data),
    modifiedBy: extractModifiedBy(data),
    modifiedOn: extractDateUpdated(data),
    preview: extractPreview(data),
    priority: extractPriority(data),
    provider: extractProviderIcon(data),
    programmingLanguage: extractProgrammingLanguage(data),
    readTime: extractReadTime(data),
    sentOn: extractSentOn(data),
    snippet: extractSummary(data) || undefined,
    // Explicitly set here to remove an empty string
    sourceBranch: extractSourceBranch(data),
    state: extractState(response, actionOptions, id, appearance, origin, fireEvent, resolve),
    subscriberCount: extractSubscriberCount(data),
    subTasksProgress: extractSubTasksProgress(data),
    storyPoints: extractStoryPoints(data),
    targetBranch: extractTargetBranch(data),
    title: extractTitle(data) || url,
    url: url,
    ari: extractAri(data)
  }, fg('platform-linking-visual-refresh-v2') && {
    type: extractType(data)
  });
};
var extractFlexibleUiContextFromEntity = function extractFlexibleUiContextFromEntity() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    actionOptions = _ref2.actionOptions,
    appearance = _ref2.appearance,
    fireEvent = _ref2.fireEvent,
    id = _ref2.id,
    origin = _ref2.origin,
    resolve = _ref2.resolve,
    response = _ref2.response;
  if (!response) {
    return undefined;
  }
  var data = response.data;
  var url = extractSmartLinkUrl(response);
  return _objectSpread({
    actions: extractFlexibleCardActions({
      response: response,
      actionOptions: actionOptions,
      id: id,
      appearance: appearance,
      fireEvent: fireEvent
    }),
    url: url,
    ari: extractSmartLinkAri(response),
    title: extractSmartLinkTitle(response) || url,
    linkIcon: extractSmartLinkIcon(response),
    preview: extractSmartLinkPreviewImage(response),
    provider: extractSmartLinkProviderIcon(response),
    modifiedOn: extractSmartLinkModifiedOn(response),
    createdOn: extractSmartLinkCreatedOn(response),
    createdBy: extractSmartLinkCreatedBy(response),
    modifiedBy: extractSmartLinkModifiedBy(response),
    // We need to add/remove these as we support new entity types
    assignedToGroup: extractPersonAssignedToAsArray(data),
    appliedToComponentsCount: extractAppliedToComponentsCount(data),
    attachmentCount: extractAttachmentCount(data),
    authorGroup: extractPersonCreatedBy(data),
    ownedByGroup: extractPersonOwnedBy(data),
    collaboratorGroup: extractPersonsUpdatedBy(data),
    commentCount: extractCommentCount(data),
    viewCount: extractViewCount(data),
    reactCount: extractReactCount(data),
    voteCount: extractVoteCount(data),
    checklistProgress: extractChecklistProgress(data),
    ownedBy: extractOwnedBy(data),
    assignedTo: extractAssignedTo(data),
    dueOn: extractDueOn(data),
    latestCommit: extractLatestCommit(data),
    location: extractLocation(data),
    priority: extractPriority(data),
    programmingLanguage: extractProgrammingLanguage(data),
    readTime: extractReadTime(data),
    sentOn: extractSentOn(data),
    snippet: extractSummary(data) || undefined,
    // Explicitly set here to remove an empty string
    sourceBranch: extractSourceBranch(data),
    state: extractState(response, actionOptions, id, appearance, origin, fireEvent, resolve),
    subscriberCount: extractSubscriberCount(data),
    subTasksProgress: extractSubTasksProgress(data),
    storyPoints: extractStoryPoints(data),
    targetBranch: extractTargetBranch(data)
  }, fg('platform-linking-visual-refresh-v2') && {
    type: extractType(data)
  });
};
var extractFlexibleUiContext = function extractFlexibleUiContext() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  if (fg('smart_links_noun_support')) {
    return extractFlexibleUiContextFromEntity(props);
  }
  return extractFlexibleUiContextFromJsonLd(props);
};
export default extractFlexibleUiContext;